import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import { colors } from '../../const/colors'
import { getHardReloadShortcut, SNOOZE_HOURS } from '../../utils/appVersion'

const NewVersionBar = ({
  stuck,
  onReload,
  onSnooze,
  onClose,
}: {
  stuck: boolean
  onReload: () => void
  onSnooze: () => void
  onClose: () => void
}) => {
  const { t } = useTranslation(['new_version', 'btn'])

  return (
    <Box
      role="status"
      aria-live="polite"
      data-e2e="new-version-bar"
      sx={{
        position: 'fixed',
        zIndex: 1200,
        left: { xs: '16px', md: '50%' },
        right: { xs: '16px', md: 'auto' },
        bottom: { xs: '16px', md: '24px' },
        transform: { xs: 'none', md: 'translateX(-50%)' },
        width: { xs: 'auto', md: 'min(680px, calc(100vw - 48px))' },
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: '14px', sm: '18px' },
        padding: { xs: '16px 18px', md: '14px 16px 14px 22px' },
        background: `linear-gradient(120deg,${colors.navyHero} 0%,${colors.navyDark} 62%)`,
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: colors.radiusBulkBar,
        boxShadow: colors.shadowHero,
        color: '#fff',
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em' }}
        >
          {t(stuck ? 'new_version:stuck_title' : 'new_version:title')}
        </Typography>
        <Typography
          data-e2e={stuck ? 'new-version-hint' : undefined}
          sx={{
            fontSize: '12px',
            lineHeight: 1.5,
            marginTop: '2px',
            color: 'rgba(255,255,255,0.72)',
          }}
        >
          {stuck
            ? t('new_version:stuck_content', {
                shortcut: getHardReloadShortcut(),
              })
            : t('new_version:content')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          alignSelf: { xs: 'stretch', sm: 'auto' },
          flexShrink: 0,
        }}
      >
        {!stuck && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={onReload}
            data-e2e="new-version-reload"
            sx={{
              // on a phone the primary action takes its own row above the two
              // quiet ones, so nothing is squeezed to fit
              order: { xs: 1, sm: 2 },
              flexBasis: { xs: '100%', sm: 'auto' },
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              padding: '9px 18px',
              borderRadius: colors.radiusBtn,
              background: colors.gold,
              color: colors.navyDark,
              boxShadow: colors.shadowKpiGold,
              '&:hover': { background: colors.goldLight },
            }}
          >
            {t('btn:refresh_app')}
          </Button>
        )}
        <Button
          variant="text"
          onClick={onSnooze}
          data-e2e="new-version-snooze"
          title={`${t('new_version:remind_hint', { hours: SNOOZE_HOURS })}`}
          sx={{
            order: { xs: 2, sm: 1 },
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            padding: '9px 12px',
            borderRadius: colors.radiusBtn,
            color: 'rgba(255,255,255,0.72)',
            '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.08)' },
          }}
        >
          {t('btn:remind_later')}
        </Button>
        <IconButton
          onClick={onClose}
          data-e2e="new-version-close"
          aria-label={`${t('btn:close')}`}
          title={`${t('btn:close')}`}
          sx={{
            order: 3,
            marginLeft: { xs: 'auto', sm: 0 },
            color: 'rgba(255,255,255,0.55)',
            '&:hover': {
              color: '#fff',
              background: 'rgba(255,255,255,0.08)',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  )
}

export default NewVersionBar
